import { Box, Tooltip } from '@mui/material';
import { Chip } from '@molecules';
import { formatDate } from '@utils';
import { PendingProspect, PendingProspectStatus } from './data/useGetPendingProspects';
import { SelectedPendingProspect } from './data/useResolvePendingProspect/hooks/useResolvePendingProspect';
import ActionButtons from './modules/ActionButtons';
import styles from './styles';

export const DEFAULT_ROWS_PER_PAGE = 10;

export const STATUS_OPTIONS: { value: PendingProspectStatus; label: string }[] = [
  { value: 'PENDING', label: 'PENDING_PROSPECTS:STATUS:PENDING' },
  { value: 'APPROVED', label: 'PENDING_PROSPECTS:STATUS:APPROVED' },
  { value: 'REJECTED', label: 'PENDING_PROSPECTS:STATUS:REJECTED' },
];

const STATUS_CHIP: Record<
  PendingProspectStatus,
  { label: string; color: 'default' | 'success' | 'error' }
> = {
  PENDING: {
    label: 'PENDING_PROSPECTS:STATUS:PENDING',
    color: 'default',
  },
  APPROVED: {
    label: 'PENDING_PROSPECTS:STATUS:APPROVED',
    color: 'success',
  },
  REJECTED: {
    label: 'PENDING_PROSPECTS:STATUS:REJECTED',
    color: 'error',
  },
};

/**
 * Pending prospects table columns
 */
export const pendingProspectsColumns = [
  {
    title: 'COMMON:COMPANY',
    content: ({ prospect }: { prospect: PendingProspect }) => {
      const companyName = prospect.customerName || '-';
      const isMaxCharacter = companyName.length > 20;
      return (
        <Tooltip title={isMaxCharacter && companyName} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{companyName}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:SEGMENT',
    content: ({ prospect }: { prospect: PendingProspect }) => {
      const segmentName = prospect.segmentName || '-';
      const isMaxCharacter = segmentName.length > 20;
      return (
        <Tooltip title={isMaxCharacter && segmentName} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{segmentName}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'PENDING_PROSPECTS:USERS_COUNT',
    content: ({ prospect }: { prospect: PendingProspect }) => (
      <Box sx={styles.centerText}>{prospect.usersCount ?? '-'}</Box>
    ),
  },
  {
    title: 'COMMON:STATUS',
    content: ({ prospect }: { prospect: PendingProspect }) => {
      const statusProps = STATUS_CHIP[prospect.status];
      if (!statusProps) return '-';
      return <Chip {...statusProps} variant='outlined' sx={styles.chip} />;
    },
  },
  {
    title: 'PENDING_PROSPECTS:CREATED_AT',
    content: ({ prospect }: { prospect: PendingProspect }) => {
      const createdAt = (prospect.createdAt && formatDate(prospect.createdAt)) || '-';
      const isMaxCharacter = createdAt.length > 15;
      return (
        <Tooltip title={isMaxCharacter && createdAt} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{createdAt}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:CREATED_BY',
    content: ({ prospect }: { prospect: PendingProspect }) => {
      const createdBy = prospect.createdBy || '-';
      const isMaxCharacter = createdBy.length > 20;
      return (
        <Tooltip title={isMaxCharacter && createdBy} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{createdBy}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:ACTIONS',
    content: ({
      prospect,
      callback,
    }: {
      prospect: PendingProspect;
      callback: (d: SelectedPendingProspect) => void;
    }) => <ActionButtons {...{ prospect, callback }} />,
  },
];
